import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PaginationInterfaceLF, ReportInterace } from "app/types";
import {
  Promotions,
  PromotionsByGroupIdFilters,
  PromotionsGroup,
  PromotionsGroupPayload,
  PromotionStatusApp,
} from "app/types/promotions";
import { AppDispatch } from "../store";
import { RootState } from "../reducers/hooks";
import { createQuery, getActiveConfig } from "app/utils/constants/common";
import {
  getGroupPromotionReportAPI,
  getPromotionsByPromotionGroupIdApi,
  getPromotionsGroupApi,
  getPromotionsGroupByIdApi,
  patchPromotionStatusByIdApi,
  postPromotionsGroupApi,
  putPromotionsGroupApi,
} from "app/services/promotions";
import { alertMessage } from "../actions/common";
import { errorHandler } from "app/shared/Service/errorHandler";

interface PromotionsGroupFilters {
  search?: string;
  status?: string;
  type?: string;
  category?: string;
  source?: string;
  fromDate?: string;
  toDate?: string;
}

interface InitialState {
  promotionsGroupList: PromotionsGroup[];
  promotionsGroupPagination: PaginationInterfaceLF | null;
  promotionsGroupListRowPerPage: number;
  isSubmitting: boolean;
  isPromotionGroupListLoading: boolean;

  filters: PromotionsGroupFilters | null;

  promotionsByGroupIdList: Promotions[];
  promotionsByGroupIdPagination: PaginationInterfaceLF;
  promotionsByGroupIdListRowsPerPage: number;
  promotionsByGroupIdFilters: PromotionsByGroupIdFilters | null;
  isPromotionByGroupIdListLoading: boolean;
  isGroupPromotionReportLoading: boolean;
}

const initialState: InitialState = {
  promotionsGroupList: [],
  promotionsGroupPagination: null,
  promotionsGroupListRowPerPage: 20,
  isSubmitting: false,
  isPromotionGroupListLoading: false,
  filters: null,

  promotionsByGroupIdList: [],
  promotionsByGroupIdPagination: null,
  promotionsByGroupIdListRowsPerPage: 20,
  promotionsByGroupIdFilters: null,
  isPromotionByGroupIdListLoading: false,
  isGroupPromotionReportLoading: false,
};

export const promotionsGroupSlice = createSlice({
  name: "groupPromotions",
  initialState,
  reducers: {
    setPromotionsGroupList: (
      state,
      action: PayloadAction<{
        promotions: PromotionsGroup[];
        pagination: PaginationInterfaceLF;
      }>
    ) => {
      state.promotionsGroupList = action.payload.promotions;
      state.promotionsGroupPagination = action.payload.pagination;
    },
    setPromotionsGroupListRowPerPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.promotionsGroupListRowPerPage = action.payload;
    },
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setIsPromotionGroupListLoading: (state, action: PayloadAction<boolean>) => {
      state.isPromotionGroupListLoading = action.payload;
    },
    setPromotionGroupFilters: (
      state,
      action: PayloadAction<PromotionsGroupFilters | null>
    ) => {
      state.filters = action.payload;
    },
    updatePromotionGroupById: (
      state,
      action: PayloadAction<PromotionsGroup>
    ) => {
      const index = state.promotionsGroupList.findIndex(
        (p: PromotionsGroup) => p.id === action.payload.id
      );
      state.promotionsGroupList[index] = action.payload;
    },

    // Promotions by Group Id
    setPromotionsByGroupIdList: (
      state,
      action: PayloadAction<{
        promotions: Promotions[];
        pagination: PaginationInterfaceLF;
      }>
    ) => {
      state.promotionsByGroupIdList = action.payload.promotions;
      state.promotionsByGroupIdPagination = action.payload.pagination;
    },
    setPromotionsByGroupIdListRowPerPage: (
      state,
      action: PayloadAction<number>
    ) => {
      state.promotionsByGroupIdListRowsPerPage = action.payload;
    },
    setPromotionsByGroupIdFilters: (
      state,
      action: PayloadAction<PromotionsByGroupIdFilters | null>
    ) => {
      state.promotionsByGroupIdFilters = action.payload;
    },
    setIsPromotionByGroupIdListLoading: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isPromotionByGroupIdListLoading = action.payload;
    },

    updatePromotionByGroupIdStatus: (
      state,
      action: PayloadAction<{ promotionId: number; status: PromotionStatusApp }>
    ) => {
      const { promotionId, status } = action.payload;
      const promotion = state.promotionsByGroupIdList.find(
        (p) => p.id === promotionId
      );
      if (promotion) {
        promotion.status = status;
      }
    },
    setIsPromotionByGroupIdReportLoading: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isGroupPromotionReportLoading = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setPromotionsGroupList,
  setPromotionsGroupListRowPerPage,
  setIsSubmitting,
  setIsPromotionGroupListLoading,
  setPromotionGroupFilters,
  updatePromotionGroupById,
  setPromotionsByGroupIdList,
  setPromotionsByGroupIdListRowPerPage,
  setPromotionsByGroupIdFilters,
  setIsPromotionByGroupIdListLoading,
  updatePromotionByGroupIdStatus,
  setIsPromotionByGroupIdReportLoading,
} = promotionsGroupSlice.actions;
export default promotionsGroupSlice.reducer;

type GetPromotionsGroupsListAction = {
  continutationToken?: string;
};
export const getPromotionsGroupListAction = ({
  continutationToken = null,
}: GetPromotionsGroupsListAction) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { storeId } = getActiveConfig();
      const { filters, promotionsGroupListRowPerPage } =
        getState().promotionsGroup;

      const customFields = [
        {
          customKey: "fromDate",
          newValue: "validFrom",
          valueType: "text" as const,
        },
        {
          customKey: "toDate",
          newValue: "validUntil",
          valueType: "text" as const,
        },
      ];
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            ...filters,
            limit: promotionsGroupListRowPerPage,
            continutationToken,
          },
          customFields,
        });

      dispatch(setIsPromotionGroupListLoading(true));
      const { data } = await getPromotionsGroupApi(storeId, query);
      dispatch(setIsPromotionGroupListLoading(false));

      const pagination = data.pagination;
      const promotions = data.data;

      dispatch(setPromotionsGroupList({ promotions, pagination }));
    } catch (error) {
      dispatch(setIsPromotionGroupListLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postPromotionsGroupAction = (payload: PromotionsGroupPayload) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsSubmitting(true));
      const { data } = await postPromotionsGroupApi({
        payload,
        storeId,
      });
      dispatch(setIsSubmitting(false));
      dispatch(alertMessage("Promotion added successfully", "success"));
      return data;
    } catch (error) {
      dispatch(setIsSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const putPromotionsGroupAction = ({
  promotionGroupId,
  payload,
}: {
  promotionGroupId: number;
  payload: PromotionsGroupPayload;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsSubmitting(true));
      const { data } = await putPromotionsGroupApi({
        payload,
        storeId,
        promotionGroupId,
      });
      dispatch(setIsSubmitting(false));
      dispatch(alertMessage("Promotion updated successfully", "success"));
      return data.data;
    } catch (error) {
      dispatch(setIsSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getPromotionGroupByIdAction = (promotionGroupId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      dispatch(setIsSubmitting(true));
      const { data } = await getPromotionsGroupByIdApi({
        storeId,
        promotionId: promotionGroupId,
      });
      dispatch(setIsSubmitting(false));
      return data;
    } catch (error) {
      dispatch(setIsSubmitting(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getPromotionsByPromotionGroupIdAction = ({
  promotionGroupId,
  continutationToken = null,
}: {
  promotionGroupId: number;
  continutationToken?: string | null;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { storeId } = getActiveConfig();
      const { promotionsByGroupIdListRowsPerPage, promotionsByGroupIdFilters } =
        getState().promotionsGroup;
      let query = "";
      query =
        "?" +
        createQuery({
          filter: {
            ...(promotionsByGroupIdFilters || {}),
            limit: promotionsByGroupIdListRowsPerPage,
            continutationToken,
          },
        });
      dispatch(setIsPromotionByGroupIdListLoading(true));
      const { data } = await getPromotionsByPromotionGroupIdApi({
        storeId,
        promotionGroupId,
        query,
      });
      dispatch(setIsPromotionByGroupIdListLoading(false));
      dispatch(
        setPromotionsByGroupIdList({
          promotions: data.data,
          pagination: data.pagination,
        })
      );
      return data;
    } catch (error) {
      dispatch(setIsPromotionByGroupIdListLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const patchPromotionStatusByIdAction = ({
  promotionId,
  status,
}: {
  promotionId: number;
  status: PromotionStatusApp;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();

      await patchPromotionStatusByIdApi({
        storeId,
        promotionId,
        payload: { status },
      });

      dispatch(
        updatePromotionByGroupIdStatus({
          promotionId,
          status,
        })
      );
      dispatch(
        alertMessage("Promotion status updated successfully", "success")
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
interface ReportRequestPayload {
  statuses: string[];
}
export const getGroupPromotionReportAction = (
  storeId: number,
  payload: ReportRequestPayload,
  groupId: number
) => {
  return async (dispatch: AppDispatch): Promise<ReportInterace> => {
    try {
      const query =
        "?" +
        createQuery({
          filter: payload,
        });
      dispatch(setIsPromotionByGroupIdReportLoading(true));
      const {
        data: { data },
      } = await getGroupPromotionReportAPI(query, storeId, groupId);
      dispatch(setIsPromotionByGroupIdReportLoading(false));
      return data;
    } catch (error) {
      dispatch(setIsPromotionByGroupIdReportLoading(false));
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
